import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProgramDetailModal from "./ProgramDetailModal";
import { CheckCircle2, Play, ChevronRight } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

/**
 * Permanent list of an athlete's training programs (current + completed).
 * listEndpoint -> {programs:[...]}. detailEndpoint(program_id) -> string url.
 */
export default function ProgramHistoryList({ listEndpoint, detailEndpoint, testidPrefix = "history" }) {
  const [programs, setPrograms] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(listEndpoint).then(r => { if (alive) setPrograms(r.data.programs || []); }).catch(() => { if (alive) setPrograms([]); });
    return () => { alive = false; };
  }, [listEndpoint]);

  if (programs === null) return <div className="text-[#888] font-mono-jp text-xs uppercase tracking-widest py-3">Loading history…</div>;
  if (programs.length === 0) return <div className="text-[#888] text-sm py-3" data-testid={`${testidPrefix}-empty`}>No programs yet.</div>;

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
      {programs.map(p => {
        const range = p.status === "current"
          ? `${fmtDate(p.started_at) || "—"} → Current`
          : `${fmtDate(p.started_at) || "—"} → ${fmtDate(p.ended_at) || "—"}`;
        return (
          <button
            key={p.program_id + (p.started_at || "")}
            onClick={() => setOpen(p)}
            className="w-full text-left bg-[#0e0e0e] border border-[#1f1f1f] hover:border-[#d7ff00]/40 p-3 flex items-center gap-3 transition"
            data-testid={`${testidPrefix}-item-${p.program_id}`}
          >
            {p.status === "current"
              ? <Play className="w-4 h-4 text-[#d7ff00] flex-shrink-0" />
              : <CheckCircle2 className="w-4 h-4 text-[#7fffd4] flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm sm:text-base uppercase truncate">{p.title}</div>
              <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666] mt-0.5">{range}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-display text-base text-[#d7ff00]">{p.compliance_pct != null ? `${p.compliance_pct}%` : "—"}</div>
              <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#666]">{p.total_distance_km != null ? `${p.total_distance_km} km` : ""}</div>
            </div>
            <span className={`font-mono-jp text-[8px] uppercase tracking-widest px-1.5 py-0.5 flex-shrink-0 ${p.status === "current" ? "bg-[#d7ff00]/15 text-[#d7ff00]" : "bg-[#7fffd4]/10 text-[#7fffd4]"}`}>
              {p.status === "current" ? "Current" : "Done"}
            </span>
            <ChevronRight className="w-4 h-4 text-[#666] flex-shrink-0" />
          </button>
        );
      })}

      {open && (
        <ProgramDetailModal
          endpoint={detailEndpoint(open.program_id)}
          title={open.title}
          status={open.status}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
